/**
 * QuizQuestion Component
 *
 * Displays a single quiz question with 4 options (A-D).
 * Highlights selected option with Emerald accent.
 * Supports keyboard shortcuts 1-4 for option selection.
 */

import { useEffect, useCallback } from 'react';
import type { QuizQuestion as QuizQuestionType } from '../lib/types';

interface QuizQuestionProps {
  question: QuizQuestionType;
  selectedIndex: number | null;
  onSelect: (index: number) => void;
  disabled?: boolean;
}

const OPTION_LETTERS = ['A', 'B', 'C', 'D'];

export function QuizQuestion({
  question,
  selectedIndex,
  onSelect,
  disabled = false,
}: QuizQuestionProps) {
  // Sort options by index to ensure correct order
  const sortedOptions = [...question.options].sort((a, b) => a.index - b.index);

  // Keyboard shortcuts: 1, 2, 3, 4 for options A, B, C, D
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (disabled) return;

      const key = event.key;
      if (['1', '2', '3', '4'].includes(key)) {
        event.preventDefault();
        const index = parseInt(key, 10) - 1;
        const option = sortedOptions[index];
        if (index >= 0 && index < sortedOptions.length && option) {
          onSelect(option.index);
        }
      }
    },
    [disabled, sortedOptions, onSelect]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  return (
    <div className="quiz-question">
      {/* Difficulty badge */}
      <div className="quiz-question-meta">
        <span className={`quiz-difficulty quiz-difficulty-${question.difficulty}`}>
          {question.difficulty}
        </span>
      </div>

      {/* Question text */}
      <h2 className="quiz-question-text">{question.questionText}</h2>

      {/* Options */}
      <div className="quiz-options">
        {sortedOptions.map((option, idx) => {
          const isSelected = selectedIndex === option.index;
          return (
            <button
              key={option.index}
              className={`quiz-option ${isSelected ? 'selected' : ''}`}
              onClick={() => onSelect(option.index)}
              disabled={disabled}
              aria-pressed={isSelected}
            >
              <span className="quiz-option-letter">{OPTION_LETTERS[idx]}</span>
              <span className="quiz-option-text">{option.text}</span>
              {isSelected && (
                <span className="quiz-option-check" aria-hidden="true">
                  ✓
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Keyboard hint */}
      <div className="quiz-keyboard-hint">
        Press 1-4 to select an option
      </div>
    </div>
  );
}
